/**
 * Overlay Dock Store
 *
 * Global persisted UI state for the floating overlay dock:
 * - Which overlay tools are open (stacked panels)
 * - Per-tool persisted UI state (e.g., selected listId)
 */

import {create} from 'zustand';
import {persist} from 'zustand/middleware';

const storage = {
	getItem: key => {
		try {
			const value = localStorage.getItem(key);
			return value ? JSON.parse(value) : null;
		} catch {
			return null;
		}
	},
	setItem: (key, value) => {
		try {
			localStorage.setItem(key, JSON.stringify(value));
		} catch {
			// ignore storage write errors (private mode/quota)
		}
	},
	removeItem: key => {
		try {
			localStorage.removeItem(key);
		} catch {
			// ignore
		}
	},
};

const useOverlayDockStore = create(
	persist(
		(set, get) => ({
			openToolIds: [],
			toolStateById: {},

			openTool: toolId =>
				set(state => {
					// If already open, move to end (top of stack visually, depending on corner)
					const next = state.openToolIds.filter(id => id !== toolId);
					next.push(toolId);
					return {openToolIds: next};
				}),

			closeTool: toolId =>
				set(state => ({openToolIds: state.openToolIds.filter(id => id !== toolId)})),

			isToolOpen: toolId => get().openToolIds.includes(toolId),

			setToolState: (toolId, nextPartialState) =>
				set(state => ({
					toolStateById: {
						...state.toolStateById,
						[toolId]: {
							...(state.toolStateById?.[toolId] || {}),
							...nextPartialState,
						},
					},
				})),

			clearToolState: toolId =>
				set(state => {
					const {[toolId]: _removed, ...rest} = state.toolStateById || {};
					return {toolStateById: rest};
				}),
		}),
		{
			name: 'overlay-dock',
			storage,
		},
	),
);

export default useOverlayDockStore;
